import { ArrowLeft, Printer } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import { StatementPeriodMode } from "../../types/card-statement.types";
import { StatementMonthPicker } from "./StatementMonthPicker";
import { StatementPeriodModeSwitch } from "./StatementPeriodModeSwitch";
import { StatementRangePicker } from "./StatementRangePicker";

export interface CardStatementToolbarProps {
  mode: StatementPeriodMode;
  month: string;
  from: string;
  to: string;
  cardCreatedAt: string | undefined;
  isLoading: boolean;
  canPrint: boolean;
  onModeChange: (mode: StatementPeriodMode) => void;
  onMonthChange: (month: string) => void;
  onRangeChange: (range: { from: string; to: string }) => void;
  onBack: () => void;
  onDownload: () => void;
}

export const CardStatementToolbar = ({
  mode,
  month,
  from,
  to,
  cardCreatedAt,
  isLoading,
  canPrint,
  onModeChange,
  onMonthChange,
  onRangeChange,
  onBack,
  onDownload,
}: CardStatementToolbarProps) => {
  const { t } = useTranslations("cards");

  return (
    <div className="statement-toolbar sticky top-0 z-10 border-b border-border bg-card/95 px-6 py-3 backdrop-blur print:hidden">
      <div className="mx-auto flex max-w-[900px] flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <ColocaButton variant="default" size="sm" icon={ArrowLeft} onClick={onBack}>
            {t("statement_back_to_card")}
          </ColocaButton>
          <h1 className="truncate text-sm font-semibold text-foreground">{t("statement_page_title")}</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <StatementPeriodModeSwitch value={mode} onChange={onModeChange} disabled={isLoading} />

          {mode === StatementPeriodMode.MONTH && (
            <StatementMonthPicker
              value={month}
              onChange={onMonthChange}
              cardCreatedAt={cardCreatedAt}
              disabled={isLoading}
            />
          )}

          {mode === StatementPeriodMode.RANGE && (
            <StatementRangePicker
              from={from}
              to={to}
              onChange={onRangeChange}
              cardCreatedAt={cardCreatedAt}
              disabled={isLoading}
            />
          )}

          <ColocaButton
            variant="primary"
            size="sm"
            icon={Printer}
            loading={isLoading}
            disabled={!canPrint}
            onClick={onDownload}
          >
            {t("statement_download_pdf")}
          </ColocaButton>
        </div>
      </div>
    </div>
  );
};
